import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetInfo,
  SheetTitle,
} from '@/components/ui/sheet';
import { FC, memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { updateSelfPassword } from '@/apis/agent';
import { errorHandler } from '@/utils/request';
import { md5 } from '@/utils';

const formSchema = z
  .object({
    password: z.string().regex(/^\S{6,20}$/, '密码不能包含空格，长度为 6~20 位'),
    newPassword: z.string().regex(/^\S{6,20}$/, '密码不能包含空格，长度为 6~20 位'),
    confirmPassword: z.string().regex(/^\S{6,20}$/, '密码不能包含空格，长度为 6~20 位'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: '确认密码与新密码不一致',
    path: ['confirmPassword'],
  });

interface EditorProps {
  open: boolean;
  onOk: () => void;
  onClose: () => void;
}

const UpdatePasswordSheet: FC<EditorProps> = ({ open, onOk, onClose }) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        password: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [open]);

  const onOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      onClose();
    }
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    updateSelfPassword({
      password: md5(values.password),
      newPassword: md5(values.newPassword),
    })
      .then(() => {
        onOk();
      })
      .catch(errorHandler);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px]">
        <SheetHeader>
          <SheetTitle>修改密码</SheetTitle>
        </SheetHeader>

        <SheetInfo>
          <Form {...form}>
            <form className="space-y-[16px]" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>旧密码</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>新密码</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>确认密码</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </SheetInfo>

        <SheetFooter>
          <SheetClose asChild>
            <Button>取消</Button>
          </SheetClose>
          <Button variant="primary" onClick={form.handleSubmit(onSubmit)}>
            确认
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default memo(UpdatePasswordSheet);
